import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import styles from './DatePicker.module.css';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];
const DAYS_SHORT = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const today = () => {
  const d = new Date();
  d.setHours(0,0,0,0);
  return d;
};

const toYMD = (d) => {
  if (!d) return '';
  const year  = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day   = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fromYMD = (str) => {
  if (!str) return null;
  const [y,m,d] = str.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setHours(0,0,0,0);
  return date;
};

const formatDisplay = (d) => {
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const isSameDay = (a, b) => a && b && toYMD(a) === toYMD(b);

const MonthGrid = ({ year, month, selected, onDayClick, minDate, maxDate, isDateDisabled }) => {
  const firstDay  = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];

  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  return (
    <div className={styles.monthGrid}>
      <div className={styles.monthLabel}>{MONTHS[month]} {year}</div>
      <div className={styles.weekdayRow}>
        {DAYS_SHORT.map(d => <span key={d} className={styles.weekday}>{d}</span>)}
      </div>
      <div className={styles.daysGrid}>
        {cells.map((date, idx) => {
          if (!date) return <span key={`blank-${idx}`} className={styles.blank} />;

          const ymd        = toYMD(date);
          const isSelected = isSameDay(date, selected);
          const isDisabled = (minDate && date < minDate) || (maxDate && date > maxDate) || (isDateDisabled && isDateDisabled(ymd));
          const isToday    = isSameDay(date, today());

          const cls = [
            styles.day,
            isSelected ? styles.daySelected : '',
            isDisabled ? styles.dayDisabled : '',
            isToday    ? styles.dayToday    : '',
          ].filter(Boolean).join(' ');

          return (
            <button
              key={ymd}
              type="button"
              className={cls}
              onClick={() => !isDisabled && onDayClick(date)}
              disabled={isDisabled}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DatePicker = ({
  value = '',
  onChange,
  placeholder = 'Select date',
  disabled = false,
  minDate,
  maxDate,
  className = '',
  size = 'default',
  dropdownPosition = 'bottom',
  isDateDisabled, // optional (ymd: 'YYYY-MM-DD') => boolean, for days outside min/max that can't be picked
}) => {
  const selectedDate = value ? fromYMD(value) : null;

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear]   = useState(() => (selectedDate || today()).getFullYear());
  const [viewMonth, setViewMonth] = useState(() => (selectedDate || today()).getMonth());
  const [dropdownStyle, setDropdownStyle] = useState(null);

  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Dropdown is portaled to document.body (see render below) so it escapes
  // any ancestor's overflow/stacking context (e.g. a scrollable modal body) —
  // position must therefore be computed manually from the trigger's screen
  // position rather than via CSS positioned-ancestor offsets.
  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current?.getBoundingClientRect().height || 360;
    const fitsBelow = rect.bottom + dropdownHeight + 6 <= window.innerHeight;
    const placeAbove = dropdownPosition === 'top' || !fitsBelow;

    setDropdownStyle({
      position: 'fixed',
      top: placeAbove ? rect.top - dropdownHeight - 6 : rect.bottom + 6,
      left: rect.left,
    });
  }, [dropdownPosition]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const inWrapper = wrapperRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inWrapper && !inDropdown) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    computePosition();
    // Recompute once more after the dropdown itself has rendered, since its
    // real height (used for above-placement) is unknown until then.
    const raf = requestAnimationFrame(computePosition);
    window.addEventListener('scroll', computePosition, true);
    window.addEventListener('resize', computePosition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', computePosition, true);
      window.removeEventListener('resize', computePosition);
    };
  }, [open, computePosition]);

  useEffect(() => {
    if (selectedDate) {
      setViewYear(selectedDate.getFullYear());
      setViewMonth(selectedDate.getMonth());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else                 { setViewMonth(m => m - 1); }
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else                  { setViewMonth(m => m + 1); }
  };

  const handleDayClick = useCallback((date) => {
    onChange(toYMD(date));
    setOpen(false);
  }, [onChange]);

  const handleClear = (e) => {
    e.stopPropagation();
    if (disabled) return;
    onChange('');
  };

  const minDateObj = minDate ? fromYMD(minDate) : null;
  const maxDateObj = maxDate ? fromYMD(maxDate) : null;

  return (
    <div
      ref={wrapperRef}
      className={`${styles.wrapper} ${className} ${disabled ? styles.disabled : ''}`}
    >
      <div
        ref={triggerRef}
        className={`${styles.trigger} ${size === 'compact' ? styles.triggerCompact : ''} ${open ? styles.triggerOpen : ''}`}
        onClick={() => !disabled && setOpen(o => !o)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); !disabled && setOpen(o => !o); } }}
      >
        <Calendar className={styles.triggerIcon} size={16} />
        <span className={`${styles.triggerText} ${!selectedDate ? styles.placeholder : ''}`}>
          {formatDisplay(selectedDate) || placeholder}
        </span>
        {selectedDate && (
          <button type="button" className={styles.clearButton} onClick={handleClear} title="Clear">
            <X size={14} />
          </button>
        )}
      </div>

      {open && createPortal(
        <div
          ref={dropdownRef}
          className={styles.dropdown}
          style={dropdownStyle || { position: 'fixed', visibility: 'hidden' }}
        >
          <div className={styles.calendarArea}>
            <div className={styles.navBar}>
              <button type="button" className={styles.navBtn} onClick={prevMonth}>
                <ChevronLeft size={16} />
              </button>
              <span className={styles.navSpacer} />
              <button type="button" className={styles.navBtn} onClick={nextMonth}>
                <ChevronRight size={16} />
              </button>
            </div>

            <MonthGrid
              year={viewYear}
              month={viewMonth}
              selected={selectedDate}
              onDayClick={handleDayClick}
              minDate={minDateObj}
              maxDate={maxDateObj}
              isDateDisabled={isDateDisabled}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DatePicker;
